import {
  createContext, useContext, useState, useEffect, useCallback, useReducer, useMemo
} from 'react'

import {
  ceil,
  floor,
  multiply,
  get,
  isFunction,
  at,
  noop
} from 'lodash'

import { BettorApi, OrderApi } from '../../api'
import { useBetslipState } from '../../state'
import { reducer } from './reducer'
import {
  CASHOUT_ACTION_SET_ORDER_CASHOUT,
  CASHOUT_ACTION_SET_STATE,
  CASHOUT_ACTION_SET_MIN_ORDER_CASHOUT,
  CASHOUT_ACTION_SET_MAX_ORDER_CASHOUT,
  CASHOUT_ACTION_SET_ORDER_PARTIAL_CASHOUT,
  orderCashoutTypes,
  ERROR_CASH_OUT_NOT_ALLOWED
} from './constants'

const { ORDER_PARTIAL_CASHOUT_TYPE, ORDER_CASHOUT_TYPE } = orderCashoutTypes

const cashout = createContext()

export const CashoutContextProvider = cashout.Provider

export const useCashout = () => (
  useContext(cashout)
)

const getInitialState = (
  initialEstimatedCashOut, initialEstimatedPartialCashOut, cashOutCoefficient, amount, odds,
  minPartialCashOut
) => ({
  amount,
  estimatedCashOut: initialEstimatedCashOut,
  minPartialCashOut,

  // orderCashout start value should be equal to bet amount
  // if estimatedCashOut lower then bet amount
  orderCashout: amount < initialEstimatedCashOut ? initialEstimatedCashOut + 1 : amount,
  orderPartialCashout: amount < initialEstimatedCashOut ? initialEstimatedCashOut : amount - 1,
  orderCashoutType: ORDER_CASHOUT_TYPE,
  // equal to order cashout initial value
  minOrderCashout: ceil(amount < initialEstimatedCashOut ? initialEstimatedCashOut + 1 : amount),
  // equal to possibleWin
  maxOrderCashout: floor(multiply(amount, odds)),
  orderCashoutWarning: { },

  estimatedPartialCashOut: initialEstimatedPartialCashOut,
  partialCashoutCoefficient: cashOutCoefficient,
  partialCashOutAmount: initialEstimatedPartialCashOut,

  isOrderCashoutExpanded: false,
  isPartialCashoutExpanded: false,
  isLoading: false,
  isOrderCashoutLoading: false,
  isPartialCashoutLoading: false
})

export const createCashoutContext = ({
  initialEstimatedCashOut,
  initialEstimatedPartialCashOut,
  cashOutCoefficient,
  currencySymbol,
  betId,
  amount,
  odds,
  minPartialCashOut,

  setOrderCashoutInfoToBet = noop,
  replaceBet = noop,
  cashoutSuccessCallback = noop,
  cashoutErrorCallback = noop,
  partialCashoutSuccessCallback = noop,
  partialCashoutErrorCallback = noop,
  orderCashoutSuccessCallback = noop,
  orderCashoutErrorCallback = noop,
  orderPartialCashoutSuccessCallback = noop,
  orderPartialCashoutErrorCallback = noop,
  cashoutNotAllowedErrorCallback = noop
}) => {
  const betslipState = useBetslipState()

  const [isCashOutAllowed, isOrderCashOutAllowed, isPartialCashOutAllowed] = at(
    betslipState, [
      'settings.cashOut', 'settings.orderCashOut', 'settings.partialCashOut'
    ]
  )

  const initialState = useMemo(() => (
    getInitialState(
      initialEstimatedCashOut, initialEstimatedPartialCashOut, cashOutCoefficient, amount, odds, minPartialCashOut
    )
  ), [])

  const [state, dispatch] = useReducer(reducer, initialState)

  const {
    estimatedCashOut, estimatedPartialCashOut, orderCashout,
    isOrderCashoutExpanded, partialCashoutCoefficient, minOrderCashout, maxOrderCashout,
    orderCashoutType, orderPartialCashout
  } = state

  const [partialCashOutAmount, setPartialCashoutAmountInitial] = useState(estimatedPartialCashOut)

  const setPartialCashoutAmount = useCallback(amount => {
    setPartialCashoutAmountInitial(Math.round(amount))
  }, [setPartialCashoutAmountInitial])

  const setState = useCallback(payload => {
    dispatch({ type: CASHOUT_ACTION_SET_STATE, payload })
  }, [])

  const setMaxOrderCashout = useCallback(() => {
    dispatch({ type: CASHOUT_ACTION_SET_MAX_ORDER_CASHOUT })
  }, [])

  const setMinOrderCashout = useCallback(() => {
    dispatch({ type: CASHOUT_ACTION_SET_MIN_ORDER_CASHOUT })
  }, [])

  const setOrderCashout = useCallback(amount => {
    dispatch({
      type: CASHOUT_ACTION_SET_ORDER_CASHOUT,
      payload: amount
    })
  }, [])

  const setOrderPartialCashout = useCallback(amount => {
    dispatch({
      type: CASHOUT_ACTION_SET_ORDER_PARTIAL_CASHOUT,
      payload: amount
    })
  }, [])

  const setOrderCashoutType = useCallback(orderCashoutType => {
    setState({ orderCashoutType })
  }, [])

  const setIsOrderCashoutExpanded = useCallback(isExpanded => {
    if (isFunction(isExpanded)) {
      const isExpandedResult = isExpanded(isOrderCashoutExpanded)
      setState({ isOrderCashoutExpanded: isExpandedResult })
    } else {
      setState({ isOrderCashoutExpanded: isExpanded })
    }
  }, [isOrderCashoutExpanded])

  const setIsPartialCashoutExpanded = useCallback(isExpanded => {
    if (isFunction(isExpanded)) {
      const isExpandedResult = isExpanded(isOrderCashoutExpanded)
      setState({ isPartialCashoutExpanded: isExpandedResult })
    } else {
      setState({ isPartialCashoutExpanded: isExpanded })
    }
  }, [isOrderCashoutExpanded])

  useEffect(() => {
    setState({
      estimatedCashOut: initialEstimatedCashOut,
      estimatedPartialCashOut: initialEstimatedPartialCashOut,
      partialCashoutCoefficient: cashOutCoefficient
    })
  }, [initialEstimatedCashOut, initialEstimatedPartialCashOut, cashOutCoefficient])

  const cashoutErrorHandler = useCallback((callabck, amount, e) => {
    const code = get(e, 'code')
    if (code === ERROR_CASH_OUT_NOT_ALLOWED) {
      setState({
        estimatedPartialCashOut: 0,
        estimatedCashOut: 0,
        partialCashoutCoefficient: 0
      })
      cashoutNotAllowedErrorCallback({ amount, currencySymbol })
      return
    }
    const maxPartialCashOutAmount = get(e, 'additionalInfo.maxPartialCashOutAmount', estimatedPartialCashOut)
    const newCashOutAmount = get(e, 'additionalInfo.newCashOutAmount', estimatedCashOut)
    const newCashOutCoefficient = get(e, 'additionalInfo.cashOutCoefficient', partialCashoutCoefficient)

    setState({
      estimatedPartialCashOut: maxPartialCashOutAmount,
      estimatedCashOut: newCashOutAmount,
      partialCashoutCoefficient: newCashOutCoefficient
    })
    callabck({ amount, currencySymbol })
  }, [setState, estimatedPartialCashOut, estimatedCashOut, partialCashoutCoefficient])

  const onCashout = useCallback(async () => {
    setState({ isLoading: true })
    try {
      const resBet = await BettorApi.submitBetCashout({
        betId,
        estimatedCashOutAmount: estimatedCashOut
      })
      const bet = get(resBet, 'bet')
      replaceBet(bet, betId)

      // payout could be bigger then estimatedCashOut
      const payout = get(bet, 'betMoney.payout')
      if (cashoutSuccessCallback) {
        cashoutSuccessCallback({ amount: payout, currencySymbol })
      }
    } catch (e) {
      cashoutErrorHandler(cashoutErrorCallback, estimatedCashOut, e)
    } finally {
      setState({ isLoading: false })
    }
  }, [betId, estimatedCashOut, replaceBet, setState, cashoutErrorHandler])

  const onOrderCashout = useCallback(async () => {
    const isOrderPartialCashout = orderCashoutType === ORDER_PARTIAL_CASHOUT_TYPE
    if (isOrderPartialCashout && (orderPartialCashout > orderCashout || !orderPartialCashout)) return

    if (orderCashout < minOrderCashout) {
      setMinOrderCashout()
      return
    }

    if (orderCashout > maxOrderCashout) {
      setMaxOrderCashout()
      return
    }

    if (orderCashout <= estimatedCashOut) {
      const newCashout = estimatedCashOut + 1
      setOrderCashout(newCashout)
      return
    }

    setState({ isOrderCashoutLoading: true })
    try {
      const orderCashoutInfo = await OrderApi.submitOrderCashout({
        betId,
        estimatedCashOut,
        cashOutType: orderCashoutType,
        desiredCashOut: orderCashout,
        desiredPartialCashOut: isOrderPartialCashout ? orderPartialCashout : null
      })

      const { cashOutType, desiredCashOutAmount, desiredPartialCashOutAmount } = orderCashoutInfo

      if (setOrderCashoutInfoToBet) {
        setOrderCashoutInfoToBet(orderCashoutInfo, betId)
      }
      if (orderCashoutSuccessCallback && cashOutType === ORDER_CASHOUT_TYPE) {
        orderCashoutSuccessCallback({ amount: desiredCashOutAmount, currencySymbol })
      }
      if (orderPartialCashoutSuccessCallback && cashOutType === ORDER_PARTIAL_CASHOUT_TYPE) {
        orderPartialCashoutSuccessCallback({ amount: desiredPartialCashOutAmount, currencySymbol })
      }
    } catch (e) {
      const callback = orderCashoutType === ORDER_CASHOUT_TYPE
        ? orderCashoutErrorCallback
        : orderPartialCashoutErrorCallback
      cashoutErrorHandler(callback, orderPartialCashout, e)
    } finally {
      setState({
        isOrderCashoutLoading: false,
        isOrderCashoutExpanded: false
      })
    }
  }, [
    orderCashout, estimatedCashOut, setOrderCashout, currencySymbol,
    betId, estimatedCashOut, setIsOrderCashoutExpanded, cashoutErrorHandler, setState,
    orderPartialCashout, orderCashoutType
  ])

  const onPartialCashout = useCallback(async () => {
    if (partialCashOutAmount < minPartialCashOut || partialCashOutAmount > estimatedPartialCashOut) {
      return
    }
    setState({ isPartialCashoutLoading: true })
    try {
      const cashoutResult = await BettorApi.submitPartialBetCashout({
        betId,
        estimatedCashOutAmount: estimatedPartialCashOut,
        partialCashOutAmount
      })
      const newBetId = get(cashoutResult, 'bet.betId')
      const newBetData = await BettorApi.searchBets({
        body: {
          filter: {
            betIds: [newBetId]
          }
        },
        query: {
          view: 'FULL'
        }
      })
      const newBet = get(newBetData, 'bets.0')
      replaceBet(newBet, betId)
      if (partialCashoutSuccessCallback) {
        partialCashoutSuccessCallback({ amount: partialCashOutAmount, currencySymbol })
      }
    } catch (e) {
      cashoutErrorHandler(partialCashoutErrorCallback, partialCashOutAmount, e)
    } finally {
      setState({ isPartialCashoutLoading: false })
    }
  }, [
    betId, estimatedPartialCashOut,
    partialCashOutAmount, currencySymbol, cashoutErrorHandler
  ])

  return {
    ...state,
    betId,
    amount,
    currencySymbol,

    // cashout functions
    onCashout,
    onOrderCashout,
    onPartialCashout,

    // setters
    setIsOrderCashoutExpanded,
    setOrderCashout,
    setOrderPartialCashout,
    setOrderCashoutType,
    setIsPartialCashoutExpanded,

    // state of the partial cashout range
    partialCashOutAmount,
    minPartialCashOut,
    setPartialCashoutAmount,

    // sportsbook settings
    isCashOutAllowed,
    isOrderCashOutAllowed,
    isPartialCashOutAllowed
  }
}
